/**
 * domains/ai/thesis2/types.ts — MER Algo Footprints v7.1H
 *
 * Production-grade types for the structured AI Thesis Engine.
 * These are the public contract — all downstream consumers depend on this.
 *
 * Design principles:
 *   - Enums are string literals for JSON safety
 *   - Every section is independently nullable where evidence may be absent
 *   - audit.validation_status distinguishes AI vs fallback provenance
 */

import type { AssembledThesis } from './schema';

// ─── Core state enums ─────────────────────────────────────────────────────────

export type ThesisState =
  | 'ACTIONABLE'   // gates passed, setup ready
  | 'DEVELOPING'   // direction clear, entry not yet confirmed
  | 'WAIT'         // no edge — conditions not met
  | 'HIGH_RISK'    // setup exists but risk flags are elevated
  | 'INVALIDATED'; // prior thesis killed by price action

export type ActionPosture =
  | 'LONG'
  | 'SHORT'
  | 'NEUTRAL'
  | 'REDUCE_RISK'
  | 'NO_TRADE';

export type ThesisBias =
  | 'bullish'
  | 'bearish'
  | 'neutral'
  | 'mixed';

export type ChangeType =
  | 'NO_MATERIAL_CHANGE'
  | 'UPGRADE'
  | 'DOWNGRADE'
  | 'INVALIDATION'
  | 'BIAS_FLIP'
  | 'RISK_INCREASE'
  | 'RISK_DECREASE'
  | 'TRIGGER_APPROACHING';

export type ValidationStatus =
  | 'AI_VALIDATED'    // AI response passed full schema check
  | 'AI_REPAIRED'     // AI response was repaired after partial failure
  | 'FALLBACK'        // deterministic fallback used, AI unavailable/invalid
  | 'GATE_DOWNGRADED' // AI said ACTIONABLE but hard gates failed — state lowered
  ;

// ─── Confidence model ─────────────────────────────────────────────────────────

export interface ConfidenceBreakdown {
  structure_confidence:   number;  // 0–100  — BOS/CHOCH clarity, bias strength
  regime_confidence:      number;  // 0–100  — regime score + driver alignment
  liquidity_confidence:   number;  // 0–100  — proximity to meaningful zones
  setup_confidence:       number;  // 0–100  — setup readiness score
  risk_confidence:        number;  // 0–100  — R/R quality + stop cleanliness
  alignment_confidence:   number;  // 0–100  — cross-layer agreement
  overall_confidence:     number;  // weighted composite 0–100
  readiness_score:        number;  // 0–100  — gate-adjusted readiness
}

// ─── Entry plan ───────────────────────────────────────────────────────────────

export interface EntryPlan {
  setup_type:             string;
  entry_trigger:          string;   // specific condition or price
  entry_price_hint:       string | null;
  confirmation_needed:    string;
  timing_note:            string | null;
}

// ─── Risk plan ────────────────────────────────────────────────────────────────

export interface RiskPlan {
  invalidation_level:     string;   // specific price or event
  invalidation_reason:    string;
  stop_quality:           number;   // 0–100 from riskEngine
  trap_conflict:          boolean;
  regime_conflict:        boolean;
  primary_risk_note:      string;
}

// ─── Targets ─────────────────────────────────────────────────────────────────

export interface Targets {
  target_1:               string | null;
  target_2:               string | null;
  risk_reward:            number | null;   // numeric R:R
  target_logic:           string;
}

// ─── Management ──────────────────────────────────────────────────────────────

export interface Management {
  max_hold_note:          string;
  scale_note:             string | null;
  re_entry_note:          string | null;
  uncertainty_disclosure: string;
}

// ─── Evidence ─────────────────────────────────────────────────────────────────

export interface ThesisEvidence {
  // Layer reads (1-2 sentences each)
  smc_read:       string;
  regime_read:    string;
  liquidity_read: string;
  trap_read:      string;
  gamma_read:     string;
  setup_read:     string;

  // Supporting / conflicting factors
  supporting_factors: string[];   // top 3–5
  conflict_factors:   string[];   // top 2–3

  // Key drivers
  key_drivers:        string[];   // top 3

  // What changed since prior thesis
  what_changed:       string[];
}

// ─── Change detection ─────────────────────────────────────────────────────────

export interface ChangeDetection {
  changed:                boolean;
  change_type:            ChangeType;
  material_changes:       string[];
  previous_thesis_state:  ThesisState | null;
  previous_posture:       ActionPosture | null;
  previous_bias:          ThesisBias | null;
  conviction_delta:       number | null;   // current - previous
}

// ─── Audit ────────────────────────────────────────────────────────────────────

export interface ThesisAudit {
  thesis_id:          string;
  generated_at:       number;        // Unix ms
  model_id:           string;        // e.g. 'claude-sonnet-4-5'
  source_version:     string;        // app version
  prompt_version:     string;        // PROMPT_VERSION constant
  schema_version:     string;        // SCHEMA_VERSION constant
  input_hash:         string;        // djb2 of serialized evidence payload
  gate_results:       GateResult[];
  validation_status:  ValidationStatus;
  fallback_reason:    string | null;
  generation_ms:      number | null;
  repair_attempts:    number;        // 0 = no repair needed
}

export interface GateResult {
  gate:     string;
  passed:   boolean;
  value:    number | string | boolean | null;
  required: number | string | boolean | null;
  note:     string | null;
}

// ─── Master thesis object ─────────────────────────────────────────────────────

export interface StructuredThesis {
  // Identity
  thesis_id:            string;
  symbol:               string;
  timeframe:            string;
  timestamp:            number;

  // Core decision
  thesis_state:         ThesisState;
  action_posture:       ActionPosture;
  is_actionable:        boolean;    // convenience: thesis_state === 'ACTIONABLE'

  // Classification
  setup_classification: string;    // e.g. 'breakout_long'
  setup_name:           string;    // human-readable

  // Narrative
  summary:              string;    // 2-3 sentence core read
  decision:             string;    // one-sentence actionable decision

  // Direction
  bias:                 ThesisBias;
  conviction:           number;    // 0–100
  actionability:        number;    // 0–100

  // Plans
  entry_plan:           EntryPlan;
  risk_plan:            RiskPlan;
  targets:              Targets;
  management:           Management;

  // Evidence
  evidence:             ThesisEvidence;

  // Confidence
  confidence:           ConfidenceBreakdown;

  // Change detection
  change_detection:     ChangeDetection;

  // Audit
  audit:                ThesisAudit;
}

// ─── Evidence input (normalized engine outputs → thesis) ─────────────────────

export interface ThesisEvidenceInput {
  symbol:     string;
  timeframe:  string;
  timestamp:  number;

  // Engine outputs (all nullable — engine may not have run yet)
  smc:        import('../../../shared/types').SMCResult | null;
  regime:     import('../../../shared/types').RegimeResult | null;
  traps:      import('../../../shared/types').TrapSignal[];
  setups:     import('../../../shared/types').TradeSetup[];
  zones:      import('../../../shared/types').LiquidityZone[];
  risk:       import('../../risk/models/riskEngine').RiskAssessment | null;
  ranking:    import('../../market/engines/ranking/rankingEngine').OpportunityScore | null;
  gammaRead:  string | null;   // pre-formatted gamma context string

  // Price context
  curPrice:   number;
  curVWAP:    number | null;

  // Prior thesis for change detection
  priorThesis:  StructuredThesis | null;
  richSetup:    import('../../market/engines/setup2/types').RichSetup | null;
}

// ─── Gate configuration ───────────────────────────────────────────────────────

export interface GateConfig {
  min_setup_score:       number;   // default 50
  min_risk_reward:       number;   // default 1.5
  min_regime_quality:    number;   // default 40
  max_trap_severity:     'None' | 'Low' | 'Medium' | 'High';  // HIGH/EXTREME block
  require_structure:     boolean;  // BOS or CHOCH required
}

export const DEFAULT_GATE_CONFIG: GateConfig = {
  min_setup_score:    50,
  min_risk_reward:    1.5,
  min_regime_quality: 40,
  max_trap_severity:  'Medium',
  require_structure:  true,
};

// ─── Compile-time drift guard ─────────────────────────────────────────────────
// If StructuredThesis ever diverges from StructuredThesisSchema, the build breaks.
// This is intentional — types and schema must stay in sync.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type _ThesisSchemaCheck = AssembledThesis extends StructuredThesis
  ? StructuredThesis extends AssembledThesis
    ? true
    : never   // ← build error: StructuredThesis has fields not in schema
  : never;    // ← build error: schema has fields not in StructuredThesis
